import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Panel, PanelLabel } from "@/components/PageHeader";
import { MonthPicker } from "@/components/MonthPicker";
import { Pick } from "@/components/movements/TransactionFormDialog";
import { api, formatApiError } from "@/lib/api";
import { currentMonth, formatDateTime } from "@/lib/format";
import { AdminAlertsCard } from "@/components/AdminAlertsCard";

interface Config { mentor_model: string; mentor_system_prompt: string; versions: { id: string; created_at: string; model?: string }[]; demo_emails: string[] }
interface TestResult { answer: string; summary: string; input_tokens: number; output_tokens: number; cost: number; model: string; cache_write_tokens: number; cache_read_tokens: number }
interface UsageRow { name: string; email: string; calls: number; ok: number; user_calls: number; input_tokens: number; output_tokens: number; cost: number }

function ConfigCard({ cfg, onSaved }: { cfg: Config; onSaved: (c: Config) => void }) {
  const [model, setModel] = useState(cfg.mentor_model);
  const [prompt, setPrompt] = useState(cfg.mentor_system_prompt);
  useEffect(() => { setModel(cfg.mentor_model); setPrompt(cfg.mentor_system_prompt); }, [cfg]);
  const save = async () => { try { onSaved((await api.put<Config>("/admin/mentor/config", { mentor_model: model, mentor_system_prompt: prompt })).data); toast.success("Mentor actualizado"); } catch (e) { toast.error(formatApiError(e)); } };
  const restore = async (id: string) => { try { onSaved((await api.post<Config>(`/admin/mentor/versions/${id}/restore`)).data); toast.success("Versión restaurada"); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <Panel testId="mentor-config">
      <PanelLabel>Modelo e instrucciones del mentor</PanelLabel>
      <Input value={model} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setModel(e.target.value)} className="mt-3 h-10" data-testid="mentor-model-input" />
      <Textarea value={prompt} rows={10} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setPrompt(e.target.value)} className="mt-3 font-mono text-xs" data-testid="mentor-prompt-input" />
      <Button onClick={save} className="mt-3 bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="mentor-config-save">Guardar</Button>
      {cfg.versions.length > 0 && (
        <ul className="mt-4 space-y-1 text-sm" data-testid="mentor-versions">
          {cfg.versions.map((v) => <li key={v.id} className="flex justify-between"><span className="text-slate-500">{formatDateTime(v.created_at)} · {v.model}</span><button onClick={() => restore(v.id)} className="font-semibold text-[#1B2A4A] underline" data-testid={`mentor-restore-${v.id}`}>Regresar a esta</button></li>)}
        </ul>
      )}
    </Panel>
  );
}

function TestCard({ emails }: { emails: string[] }) {
  const [email, setEmail] = useState(emails[0] ?? "");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState<TestResult | null>(null);
  const run = async () => { setBusy(true); try { setR((await api.post<TestResult>("/admin/mentor/test", { email, question: q })).data); } catch (e) { toast.error(formatApiError(e)); } setBusy(false); };
  return (
    <Panel testId="mentor-test">
      <PanelLabel>Probar mentor</PanelLabel>
      <div className="mt-3 grid gap-3 sm:grid-cols-[220px_1fr_auto]">
        <Pick label="Usuario demo" value={email} onChange={setEmail} options={emails.map((e) => ({ value: e, label: e }))} testId="mentor-test-user" placeholder="Usuario" />
        <div className="space-y-1.5"><span className="text-sm text-slate-700">Pregunta</span><Input value={q} maxLength={500} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value)} className="h-11" data-testid="mentor-test-question" /></div>
        <Button onClick={run} disabled={busy || !q.trim()} className="h-11 self-end bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="mentor-test-run">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Probar"}</Button>
      </div>
      {r && (
        <div className="mt-4 space-y-3" data-testid="mentor-test-result">
          <p className="tabular text-xs text-slate-500" data-testid="mentor-test-cost">{r.model} · {r.input_tokens} tokens de entrada ({r.cache_read_tokens} leídos de caché, {r.cache_write_tokens} guardados en caché) · {r.output_tokens} de salida · costo ${r.cost.toFixed(4)} USD</p>
          <p className="whitespace-pre-wrap rounded-lg bg-[#F4F5F7] p-3 text-sm text-[#1B2A4A]" data-testid="mentor-test-answer">{r.answer}</p>
          <details><summary className="cursor-pointer text-sm font-semibold text-[#1B2A4A]">Resumen enviado</summary><pre className="mt-2 whitespace-pre-wrap text-xs text-slate-600" data-testid="mentor-test-summary">{r.summary}</pre></details>
        </div>
      )}
    </Panel>
  );
}

function UsageCard() {
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState<UsageRow[]>([]);
  useEffect(() => { api.get<UsageRow[]>("/admin/mentor/usage", { params: { month } }).then((r) => setRows(r.data)).catch(() => undefined); }, [month]);
  const csv = async () => {
    const r = await api.get("/admin/mentor/usage.csv", { params: { month }, responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data as Blob); a.download = `mentor-${month}.csv`; a.click();
  };
  return (
    <Panel testId="mentor-usage">
      <div className="flex flex-wrap items-center justify-between gap-3"><PanelLabel>Consultas y costo por usuario</PanelLabel><Button variant="outline" onClick={csv} className="h-9" data-testid="mentor-usage-csv"><Download className="mr-2 h-4 w-4" /> CSV</Button></div>
      <div className="mt-3"><MonthPicker month={month} onChange={setMonth} /></div>
      <ul className="mt-3 divide-y divide-[#DDE1E8] text-sm">
        {rows.map((r) => <li key={r.email} className="grid grid-cols-[1fr_auto_auto] gap-3 py-2"><span className="truncate">{r.name} <span className="text-slate-400">{r.email}</span></span><span className="tabular">{r.user_calls} consultas</span><span className="tabular font-semibold">${r.cost.toFixed(4)}</span></li>)}
        {rows.length === 0 && <li className="py-4 text-slate-500">Sin consultas este mes.</li>}
      </ul>
    </Panel>
  );
}

export function AdminMentorPanel() {
  const [cfg, setCfg] = useState<Config | null>(null);
  useEffect(() => { api.get<Config>("/admin/mentor/config").then((r) => setCfg(r.data)).catch(() => undefined); }, []);
  if (!cfg) return null;
  return (
    <div className="mt-8 grid gap-6 lg:grid-cols-2" data-testid="admin-mentor-panel">
      <div className="space-y-6"><TestCard emails={cfg.demo_emails} /><UsageCard /></div>
      <div className="space-y-6"><ConfigCard cfg={cfg} onSaved={setCfg} /><AdminAlertsCard /></div>
    </div>
  );
}
